(() => {
  const initializedRoots = new WeakSet();
  const observers = new WeakMap();
  const initializedCategoryScrollers = new WeakSet();
  const initializedMobileScrollers = new WeakSet();
  const viewStorageKey = 'soe-collection-view';

  const getSavedView = (root) => {
    try {
      return window.localStorage.getItem(viewStorageKey) || root.dataset.defaultView || 'grid';
    } catch (_error) {
      return root.dataset.defaultView || 'grid';
    }
  };

  const saveView = (view) => {
    try {
      window.localStorage.setItem(viewStorageKey, view);
    } catch (_error) {
      // Storage can be unavailable in privacy modes; the current view still works.
    }
  };

  const applyView = (root, requestedView) => {
    const view = requestedView === 'list' ? 'list' : 'grid';
    const grid = root.querySelector('#product-grid.soe-collection__grid');
    if (grid) grid.classList.toggle('is-list-view', view === 'list');

    root.querySelectorAll('[data-soe-view]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.soeView === view));
    });
  };

  const revealActiveCategory = (root) => {
    if (!window.matchMedia('(max-width: 989px)').matches) return;

    const scroller = root.querySelector('.soe-collection__categories ul');
    const activeItem = scroller?.querySelector('.soe-collection__category-link.is-active')?.closest('li');
    if (!scroller || !activeItem) return;

    window.requestAnimationFrame(() => {
      const scrollerRect = scroller.getBoundingClientRect();
      const itemRect = activeItem.getBoundingClientRect();
      const target = scroller.scrollLeft + itemRect.left - scrollerRect.left - (scroller.clientWidth - itemRect.width) / 2;
      scroller.scrollTo({ left: Math.max(0, target), behavior: 'auto' });
      updateCategoryArrows(root);
    });
  };

  const updateCategoryArrows = (root) => {
    const scroller = root.querySelector('[data-soe-category-scroller]');
    const previous = root.querySelector('[data-soe-category-scroll="previous"]');
    const next = root.querySelector('[data-soe-category-scroll="next"]');
    if (!scroller || !previous || !next) return;

    const maximum = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    previous.disabled = scroller.scrollLeft <= 2;
    next.disabled = scroller.scrollLeft >= maximum - 2 || maximum <= 2;
  };

  const initCategoryScroller = (root) => {
    const scroller = root.querySelector('[data-soe-category-scroller]');
    if (!scroller || initializedCategoryScrollers.has(scroller)) return;

    scroller.addEventListener('scroll', () => updateCategoryArrows(root), { passive: true });
    initializedCategoryScrollers.add(scroller);
    window.requestAnimationFrame(() => updateCategoryArrows(root));
  };

  const updateMobileScrollArrows = (scroller) => {
    const shell = scroller.closest('[data-soe-mobile-scroll-shell]');
    const previous = shell?.querySelector('[data-soe-mobile-scroll="previous"]');
    const next = shell?.querySelector('[data-soe-mobile-scroll="next"]');
    if (!shell || !previous || !next) return;

    const maximum = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    previous.disabled = scroller.scrollLeft <= 2;
    next.disabled = scroller.scrollLeft >= maximum - 2 || maximum <= 2;
  };

  const initMobileScrollers = (root) => {
    root.querySelectorAll('[data-soe-mobile-scroller]').forEach((scroller) => {
      if (!initializedMobileScrollers.has(scroller)) {
        scroller.addEventListener('scroll', () => updateMobileScrollArrows(scroller), { passive: true });
        initializedMobileScrollers.add(scroller);
      }
      window.requestAnimationFrame(() => updateMobileScrollArrows(scroller));
    });
  };

  const normalizedParams = (form) => {
    const params = new URLSearchParams(new FormData(form));
    Array.from(params.entries()).forEach(([key, value]) => {
      if (value === '') params.delete(key);
    });
    return params.toString();
  };

  const updateCollection = (root, searchParams, event) => {
    const controller = root.querySelector('facet-filters-form');
    if (controller && typeof controller.onSubmitForm === 'function') {
      controller.onSubmitForm(searchParams, event);
      return;
    }

    window.location.assign(`${window.location.pathname}${searchParams ? `?${searchParams}` : ''}`);
  };

  const closeQuickFilters = (root, except = null) => {
    root.querySelectorAll('[data-soe-quick-filter][open]').forEach((details) => {
      if (details !== except) details.removeAttribute('open');
    });
  };

  const initRoot = (root) => {
    root.classList.add('is-js');
    applyView(root, getSavedView(root));
    initCategoryScroller(root);
    initMobileScrollers(root);
    revealActiveCategory(root);

    if (root.dataset.filtersInitiallyOpen === 'false') {
      root.querySelectorAll('.facets__form-vertical details[open]').forEach((details) => details.removeAttribute('open'));
    }

    if (!observers.has(root)) {
      const container = root.querySelector('#ProductGridContainer');
      if (container) {
        const observer = new MutationObserver(() => {
          applyView(root, getSavedView(root));
          initMobileScrollers(root);
        });
        observer.observe(container, { childList: true, subtree: true });
        observers.set(root, observer);
      }
    }

    initializedRoots.add(root);
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-soe-collection]').forEach((root) => {
      if (!initializedRoots.has(root)) initRoot(root);
    });
  };

  document.addEventListener('click', (event) => {
    const mobileScrollArrow = event.target.closest('[data-soe-mobile-scroll]');
    if (mobileScrollArrow) {
      const shell = mobileScrollArrow.closest('[data-soe-mobile-scroll-shell]');
      const scroller = shell?.querySelector('[data-soe-mobile-scroller]');
      if (!shell || !scroller) return;

      const direction = mobileScrollArrow.dataset.soeMobileScroll === 'previous' ? -1 : 1;
      scroller.scrollBy({ left: direction * Math.max(150, scroller.clientWidth * 0.7), behavior: 'smooth' });
      return;
    }

    const categoryArrow = event.target.closest('[data-soe-category-scroll]');
    if (categoryArrow) {
      const root = categoryArrow.closest('[data-soe-collection]');
      const scroller = root?.querySelector('[data-soe-category-scroller]');
      if (!root || !scroller) return;

      const direction = categoryArrow.dataset.soeCategoryScroll === 'previous' ? -1 : 1;
      scroller.scrollBy({ left: direction * Math.max(180, scroller.clientWidth * 0.72), behavior: 'smooth' });
      return;
    }

    const viewButton = event.target.closest('[data-soe-view]');
    if (viewButton) {
      const root = viewButton.closest('[data-soe-collection]');
      const view = viewButton.dataset.soeView;
      saveView(view);
      applyView(root, view);
      return;
    }

    const filterSummary = event.target.closest('[data-soe-quick-filter] > summary');
    if (filterSummary) {
      const root = filterSummary.closest('[data-soe-collection]');
      closeQuickFilters(root, filterSummary.parentElement);
      return;
    }

    const activeFilterLink = event.target.closest('.soe-collection__active-chip, .soe-collection__clear-filters');
    if (activeFilterLink) {
      const root = activeFilterLink.closest('[data-soe-collection]');
      if (!root) return;
      event.preventDefault();
      const targetUrl = new URL(activeFilterLink.href, window.location.origin);
      updateCollection(root, targetUrl.search.slice(1), event);
      return;
    }

    const loadMoreButton = event.target.closest('[data-soe-load-more]');
    if (loadMoreButton) {
      const root = loadMoreButton.closest('[data-soe-collection]');
      const status = root.querySelector('[data-soe-load-status]');
      const fallback = root.querySelector('.soe-collection__pagination-fallback');
      const nextUrl = loadMoreButton.dataset.nextUrl;
      if (!nextUrl || loadMoreButton.disabled) return;

      loadMoreButton.disabled = true;
      loadMoreButton.classList.add('is-loading');
      if (status) status.textContent = 'Cargando más productos';

      fetch(nextUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.text();
        })
        .then((html) => {
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const incomingGrid = parsed.querySelector('#product-grid.soe-collection__grid');
          const currentGrid = root.querySelector('#product-grid.soe-collection__grid');
          if (!incomingGrid || !currentGrid) throw new Error('Product grid not found');

          const existingIds = new Set(
            Array.from(currentGrid.querySelectorAll('[data-product-id]')).map((item) => item.dataset.productId)
          );
          let added = 0;
          incomingGrid.querySelectorAll(':scope > [data-product-id]').forEach((item) => {
            if (!existingIds.has(item.dataset.productId)) {
              currentGrid.appendChild(item);
              existingIds.add(item.dataset.productId);
              added += 1;
            }
          });

          const followingUrl = incomingGrid.dataset.nextUrl;
          if (followingUrl) {
            loadMoreButton.dataset.nextUrl = followingUrl;
            loadMoreButton.disabled = false;
            loadMoreButton.classList.remove('is-loading');
          } else {
            loadMoreButton.remove();
          }

          applyView(root, getSavedView(root));
          history.replaceState({ searchParams: new URL(nextUrl, window.location.origin).search.slice(1) }, '', nextUrl);
          if (status) status.textContent = `${added} productos añadidos`;
        })
        .catch(() => {
          loadMoreButton.disabled = false;
          loadMoreButton.classList.remove('is-loading');
          if (fallback) fallback.style.display = 'block';
          if (status) status.textContent = 'No fue posible cargar más productos. Utiliza la paginación disponible.';
        });
    }

    document.querySelectorAll('[data-soe-collection]').forEach((root) => {
      if (!event.target.closest('[data-soe-quick-filter]')) closeQuickFilters(root);
    });
  });

  document.addEventListener('change', (event) => {
    const form = event.target.closest('[data-soe-quick-filters]');
    if (!form || event.target.matches('.field__input')) return;
    form.requestSubmit();
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-soe-quick-filters]');
    if (!form) return;
    event.preventDefault();
    const root = form.closest('[data-soe-collection]');
    closeQuickFilters(root);
    updateCollection(root, normalizedParams(form), event);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const openFilter = event.target.closest('[data-soe-quick-filter][open]');
    if (!openFilter) return;
    openFilter.removeAttribute('open');
    openFilter.querySelector('summary')?.focus();
  });

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  window.addEventListener('resize', () => {
    document.querySelectorAll('[data-soe-collection]').forEach((root) => {
      updateCategoryArrows(root);
      initMobileScrollers(root);
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }
})();
